<template>
  <div class="cyber-modal-overlay" v-if="visible" @click="handleOverlayClick">
    <div class="cyber-modal" @click.stop>
      <div class="decorative-corner top-left"></div>
      <div class="decorative-corner top-right"></div>
      <div class="decorative-corner bottom-left"></div>
      <div class="decorative-corner bottom-right"></div>
      
      <div class="modal-header">
        <div class="terminal-prompt">
          <span class="prompt-symbol">></span>
          <span class="prompt-text">{{ title }}</span>
        </div>
        <button class="close-button" @click="close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="4" y1="4" x2="12" y2="12"></line>
            <line x1="12" y1="4" x2="4" y2="12"></line>
          </svg>
        </button>
      </div>
      
      <div class="modal-body">
        <div class="message-content">
          <svg class="message-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12" y2="16"></line>
          </svg>
          <p class="message-text">{{ message }}</p>
        </div>
      </div>
      
      <div class="modal-footer">
        <button class="cyber-button primary" @click="handleConfirm">
          <span class="button-text">确 认</span>
          <span class="button-accent">CONFIRM</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CyberModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '系统提示'
    },
    message: {
      type: String,
      default: ''
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    handleConfirm() {
      this.$emit('confirm');
      this.close();
    },
    handleOverlayClick() {
      this.close();
    }
  }
}
</script>

<style scoped>
.cyber-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.cyber-modal {
  position: relative;
  width: 100%;
  max-width: 480px;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 
    0 0 60px rgba(0, 212, 255, 0.2),
    inset 0 0 40px rgba(0, 0, 0, 0.5);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from { 
    opacity: 0; 
    transform: translateY(-20px) scale(0.95);
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1);
  }
}

.decorative-corner {
  position: absolute;
  width: 20px;
  height: 20px;
}

.decorative-corner::before,
.decorative-corner::after {
  content: '';
  position: absolute;
  background: var(--accent);
  opacity: 0.6;
}

.decorative-corner.top-left {
  top: 8px;
  left: 8px;
}
.decorative-corner.top-left::before {
  top: 0;
  left: 0;
  width: 2px;
  height: 16px;
}
.decorative-corner.top-left::after {
  top: 0;
  left: 0;
  width: 16px;
  height: 2px;
}

.decorative-corner.top-right {
  top: 8px;
  right: 8px;
}
.decorative-corner.top-right::before {
  top: 0;
  right: 0;
  width: 2px;
  height: 16px;
}
.decorative-corner.top-right::after {
  top: 0;
  right: 0;
  width: 16px;
  height: 2px;
}

.decorative-corner.bottom-left {
  bottom: 8px;
  left: 8px;
}
.decorative-corner.bottom-left::before {
  bottom: 0;
  left: 0;
  width: 2px;
  height: 16px;
}
.decorative-corner.bottom-left::after {
  bottom: 0;
  left: 0;
  width: 16px;
  height: 2px;
}

.decorative-corner.bottom-right {
  bottom: 8px;
  right: 8px;
}
.decorative-corner.bottom-right::before {
  bottom: 0;
  right: 0;
  width: 2px;
  height: 16px;
}
.decorative-corner.bottom-right::after {
  bottom: 0;
  right: 0;
  width: 16px;
  height: 2px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 32px 16px;
  border-bottom: 1px solid rgba(0, 212, 255, 0.2);
}

.terminal-prompt {
  display: flex;
  align-items: center;
  gap: 8px;
}

.prompt-symbol {
  color: var(--accent);
  font-family: 'JetBrains Mono', monospace;
  font-weight: bold;
  animation: blinkPrompt 1.5s ease-in-out infinite;
}

@keyframes blinkPrompt {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

.prompt-text {
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: 1px;
}

.close-button {
  background: transparent;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease, transform 0.2s ease;
}

.close-button:hover {
  color: var(--accent);
  transform: rotate(90deg);
}

.modal-body {
  padding: 32px;
}

.message-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
}

.message-icon {
  color: var(--accent);
  animation: pulseIcon 2s ease-in-out infinite;
}

@keyframes pulseIcon {
  0%, 100% { 
    transform: scale(1);
    filter: drop-shadow(0 0 0 rgba(0, 212, 255, 0));
  }
  50% { 
    transform: scale(1.1);
    filter: drop-shadow(0 0 20px rgba(0, 212, 255, 0.5));
  }
}

.message-text {
  font-size: 15px;
  color: var(--text-primary);
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
  text-align: left;
}

.modal-footer {
  padding: 16px 32px 24px;
  display: flex;
  justify-content: center;
}

.cyber-button {
  padding: 14px 40px;
  border: none;
  border-radius: 8px;
  font-family: 'Orbitron', sans-serif;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 2px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.cyber-button.primary {
  background: linear-gradient(135deg, var(--accent), #0099cc);
  color: #000;
}

.cyber-button.primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.cyber-button.primary:hover::before {
  left: 100%;
}

.cyber-button.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px var(--accent-glow);
}

.button-text {
  font-size: 14px;
}

.button-accent {
  font-size: 10px;
  opacity: 0.7;
}
</style>
